import * as React from 'react';
import { ApiHatasi, api, oturum } from '@/lib/api';

export interface GirisKimligi {
  kullanici: { id: string; ad: string; tenantId: string; roller: string[] };
  yetkiler: string[];
}

interface TesisBilgi {
  ad: string | null;
  slug: string | null;
  demoMu: boolean;
}

type Asama = 'form' | 'gonderiliyor' | 'basarili';

/** Saate gore selam. Gece vardiyasi da bu ekrani goruyor. */
function selamla(saat: number): string {
  if (saat < 6) return 'İyi geceler';
  if (saat < 12) return 'Günaydın';
  if (saat < 18) return 'İyi günler';
  return 'İyi akşamlar';
}

function basHarfler(ad: string): string {
  const parcalar = ad.trim().split(/\s+/).filter(Boolean);
  if (parcalar.length === 0) return '?';
  const ilk = parcalar[0]![0] ?? '';
  const son = parcalar.length > 1 ? (parcalar[parcalar.length - 1]![0] ?? '') : '';
  return (ilk + son).toLocaleUpperCase('tr-TR');
}

/**
 * Giris ekrani (otelcms Giris.dc.html tasarimindan).
 *
 * Sol sutun formu, sag sutun urunun ne yaptigini anlatir. Sagdaki sutun
 * dar ekranlarda tamamen gizlenir; telefondan giren resepsiyonist yalnizca
 * formu gorur.
 */
export function GirisEkrani({ onGiris }: { onGiris: (k: GirisKimligi) => void }) {
  const [eposta, setEposta] = React.useState('');
  const [sifre, setSifre] = React.useState('');
  const [sifreGorunur, setSifreGorunur] = React.useState(false);
  const [kalici, setKalici] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [sarsinti, setSarsinti] = React.useState(0);
  const [asama, setAsama] = React.useState<Asama>('form');
  const [tesis, setTesis] = React.useState<TesisBilgi | null>(null);
  const [not, setNot] = React.useState<string | null>(null);
  const kimlikRef = React.useRef<GirisKimligi | null>(null);

  const [simdi, setSimdi] = React.useState(() => new Date());
  React.useEffect(() => {
    const t = setInterval(() => setSimdi(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);

  // Adresin zaten soyledigi bilgiyi (tesis adi) giris oncesi gosteririz.
  React.useEffect(() => {
    let iptal = false;
    api<TesisBilgi>('/api/tesis-bilgi')
      .then((b) => !iptal && setTesis(b))
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, []);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (asama !== 'form') return;
    setAsama('gonderiliyor');
    setHata(null);
    setNot(null);
    try {
      const cevap = await api<{ access: string; refresh: string } & GirisKimligi>('/api/auth/giris', {
        method: 'POST',
        body: JSON.stringify({ eposta, sifre }),
      });
      oturum.yaz(cevap.access, cevap.refresh, kalici);
      kimlikRef.current = { kullanici: cevap.kullanici, yetkiler: cevap.yetkiler };
      setAsama('basarili');
      // Basari durumu kisa sure gorunur, sonra panele geceriz.
      setTimeout(() => kimlikRef.current && onGiris(kimlikRef.current), 1100);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Giriş yapılamadı. Bağlantınızı kontrol edin.');
      setSarsinti((n) => n + 1);
      setAsama('form');
    }
  }

  function demoDoldur() {
    setEposta('resepsiyon@denizbutik.example');
    setSifre('Demo1234!');
    setHata(null);
  }

  const hostAdi = typeof location !== 'undefined' ? location.host : '';
  const yukleniyor = asama === 'gonderiliyor';
  const basarili = asama === 'basarili';

  if (basarili && kimlikRef.current) {
    return (
      <Duzen simdi={simdi}>
        <div className="[animation:popin_.35s_cubic-bezier(.2,.8,.2,1)_both]">
          <div className="rounded-[20px] bg-lacivert p-6 text-white">
            <div className="flex items-center gap-3.5">
              <span className="grid size-12 shrink-0 place-items-center rounded-full bg-marka-600 text-base font-bold">
                {basHarfler(kimlikRef.current.kullanici.ad)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold">{kimlikRef.current.kullanici.ad}</span>
                <span className="block truncate text-[13px] text-mavi-acik">{eposta}</span>
              </span>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#12a150" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#12a150" strokeOpacity=".35" />
                <path d="m7.5 12.4 3 3 6-6.5" strokeDasharray="16" strokeDashoffset="16" style={{ animation: 'draw .5s .1s ease-out forwards' }} />
              </svg>
            </div>
            <p className="mt-4 text-[14px] text-mavi-acik">
              Giriş başarılı. Oda planına yönlendiriliyorsunuz…
            </p>
            <span className="mt-4 block h-1 overflow-hidden rounded-full bg-white/15">
              <span className="block h-full origin-left rounded-full bg-yesil [animation:bar_1.1s_linear_both]" />
            </span>
          </div>
          <button
            type="button"
            onClick={() => kimlikRef.current && onGiris(kimlikRef.current)}
            className="mt-4 text-[14px] font-semibold text-marka-600 hover:underline"
          >
            Hemen git →
          </button>
        </div>
      </Duzen>
    );
  }

  return (
    <Duzen simdi={simdi}>
      <span className="inline-flex items-center gap-2.5 rounded-full border border-cizgi bg-white px-3.5 py-2 text-[13px] font-semibold text-lacivert">
        <span className="relative grid size-2 place-items-center">
          <span className="absolute size-2 rounded-full bg-yesil [animation:ring_2s_ease-out_infinite]" />
          <span className="size-2 rounded-full bg-yesil" />
        </span>
        {tesis?.ad ?? hostAdi}
      </span>

      <h1 className="mt-5 text-[30px] font-bold leading-[1.15] tracking-[-.02em] text-lacivert sm:text-[34px]">
        {selamla(simdi.getHours())}, tekrar hoş geldiniz
      </h1>
      <p className="mt-2.5 text-[15px] text-gri">Vardiyaya başlamak için hesabınızla giriş yapın.</p>

      <form onSubmit={gonder} className="mt-7 flex flex-col gap-4" noValidate>
        <label className="relative block">
          <span className="sr-only">E-posta</span>
          <svg className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7d879c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
            <path d="m3.5 7 8.5 6 8.5-6" />
          </svg>
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            placeholder="E-posta adresiniz"
            value={eposta}
            onChange={(e) => setEposta(e.target.value)}
            className="w-full rounded-[14px] border-[1.5px] border-cizgi bg-white py-[15px] pl-[46px] pr-4 text-[15px] text-lacivert outline-none transition placeholder:text-gri/70 focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]"
          />
        </label>

        <label
          key={sarsinti}
          className={'relative block' + (hata ? ' [animation:shake_.4s_ease-in-out]' : '')}
        >
          <span className="sr-only">Parola</span>
          <svg className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7d879c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="10.5" width="16" height="10.5" rx="3" />
            <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
          </svg>
          <input
            type={sifreGorunur ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            required
            placeholder="Parolanız"
            value={sifre}
            onChange={(e) => setSifre(e.target.value)}
            aria-invalid={hata ? true : undefined}
            className={
              'w-full rounded-[14px] border-[1.5px] bg-white py-[15px] pl-[46px] pr-[70px] text-[15px] text-lacivert outline-none transition placeholder:text-gri/70 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)] ' +
              (hata ? 'border-kirmizi focus:border-kirmizi' : 'border-cizgi focus:border-marka-600')
            }
          />
          <button
            type="button"
            onClick={() => setSifreGorunur((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-gri hover:text-marka-600"
          >
            {sifreGorunur ? 'Gizle' : 'Göster'}
          </button>
        </label>

        {hata && (
          <p role="alert" className="flex items-start gap-2 text-[13.5px] font-medium text-kirmizi">
            <svg className="mt-px shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M12 7.5v5m0 3.5v.01" />
            </svg>
            {hata}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex cursor-pointer select-none items-center gap-2.5 text-[14px] text-gri">
            <input
              type="checkbox"
              checked={kalici}
              onChange={(e) => setKalici(e.target.checked)}
              className="peer sr-only"
            />
            <span className="grid size-5 shrink-0 place-items-center rounded-[6px] border-[1.5px] border-cizgi bg-white transition peer-checked:border-marka-600 peer-checked:bg-marka-600 peer-focus-visible:shadow-[0_0_0_4px_rgba(31,79,216,.12)]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m5 12.5 4.5 4.5L19 7" />
              </svg>
            </span>
            Bu cihazda oturumu açık tut
          </label>
          <button
            type="button"
            onClick={() =>
              setNot(
                'Parola sıfırlama henüz self-servis değil. Tesis yöneticiniz yönetim panelinden size yeni bir geçici parola oluşturabilir.',
              )
            }
            className="text-[14px] font-semibold text-marka-600 hover:underline"
          >
            Şifremi unuttum
          </button>
        </div>

        {not && (
          <p className="rounded-xl bg-mavi-zemin px-3.5 py-3 text-[13.5px] leading-relaxed text-lacivert">{not}</p>
        )}

        <button
          type="submit"
          disabled={yukleniyor}
          className="mt-1 flex items-center justify-center gap-2.5 rounded-full bg-marka-600 py-4 text-[15px] font-semibold text-white shadow-[0_14px_32px_-14px_rgba(31,79,216,.7)] transition hover:bg-marka-700 disabled:opacity-80"
        >
          {yukleniyor && (
            <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />
          )}
          {yukleniyor ? 'Giriş yapılıyor…' : 'Giriş yap'}
        </button>
      </form>

      {tesis?.demoMu && (
        <>
          <div className="my-6 flex items-center gap-3.5 text-[13px] text-gri">
            <span className="h-px flex-1 bg-cizgi" />
            veya
            <span className="h-px flex-1 bg-cizgi" />
          </div>
          <button
            type="button"
            onClick={demoDoldur}
            className="flex w-full items-center justify-center gap-2.5 rounded-full border-[1.5px] border-cizgi bg-white py-[15px] text-[15px] font-semibold text-lacivert transition hover:border-mavi-acik hover:bg-mavi-zemin"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M10 8.5v7l6-3.5z" fill="#1f4fd8" />
            </svg>
            Demo hesabıyla dene
          </button>
        </>
      )}
    </Duzen>
  );
}

/** Iki sutunlu kabuk: solda icerik, sagda urun anlatimi. */
function Duzen({ simdi, children }: { simdi: Date; children: React.ReactNode }) {
  const saat = simdi.toLocaleString('tr-TR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex flex-col bg-white px-6 py-8 sm:px-10">
        <a href="/" className="inline-flex items-center gap-2.5 self-start text-[19px] font-bold tracking-[-.02em] text-lacivert">
          <svg width="28" height="28" viewBox="0 0 30 30" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="7" width="24" height="20" rx="4" />
            <path d="M9 7V4m12 3V4M3 13h24" />
            <path d="M10 19h3m4 0h3" stroke="#f2b632" />
          </svg>
          otelcms
        </a>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[420px] [animation:rise_.5s_ease-out_both]">{children}</div>
        </div>

        <footer className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-gri">
          <span>© {simdi.getFullYear()} otelcms</span>
          <a href="/gizlilik" className="hover:text-lacivert">Gizlilik</a>
          <a href="/kvkk" className="hover:text-lacivert">KVKK</a>
          <a href="mailto:destek@otelcms.com" className="hover:text-lacivert">Destek</a>
        </footer>
      </div>

      <div className="relative hidden overflow-hidden bg-lacivert px-12 py-10 lg:flex lg:flex-col">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[.35]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 size-[520px] rounded-full [animation:drift_18s_ease-in-out_infinite]"
          style={{ background: 'radial-gradient(circle, rgba(31,79,216,.55), transparent 65%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-20 -left-20 size-[360px] rounded-full [animation:drift_22s_ease-in-out_infinite_reverse]"
          style={{ background: 'radial-gradient(circle, rgba(242,182,50,.3), transparent 65%)' }}
        />

        <div className="relative flex items-center justify-between gap-4 text-[13px]">
          <span className="flex items-center gap-2.5 text-mavi-acik">
            <span className="size-2 rounded-full bg-yesil [animation:blink_2s_infinite]" />
            Sistem çalışıyor · KBS bağlantısı canlı
          </span>
          <span className="text-white/60">{saat}</span>
        </div>

        <div className="relative flex flex-1 flex-col justify-center">
          <CheckInCizimi />
          <h2 className="mt-10 max-w-[15ch] text-[34px] font-bold leading-[1.15] tracking-[-.02em] text-white">
            Kimliği gösterin, gerisi kendiliğinden olsun.
          </h2>
          <p className="mt-4 max-w-[46ch] text-[15px] leading-relaxed text-mavi-acik">
            Kimlik okuma, KBS bildirimi ve oda planı tek ekranda. Yeni personel 5 dakikada öğrenir.
          </p>

          <div className="mt-9 grid grid-cols-3 gap-3.5">
            {(
              [
                ['3 adım', 'check-in ekranının tamamı'],
                ['24 saat', 'kimlik görselinin saklanma süresi'],
                ['04:00', 'otomatik gece kapanışı'],
              ] as const
            ).map(([buyuk, kucuk], i) => (
              <div
                key={buyuk}
                className="rounded-2xl border border-white/10 bg-white/[.06] p-4 backdrop-blur-sm [animation:rise_.6s_ease-out_both]"
                style={{ animationDelay: `${0.15 * i + 0.2}s` }}
              >
                <span className="block text-[22px] font-bold tracking-[-.02em] text-white">{buyuk}</span>
                <span className="mt-1 block text-[12.5px] leading-snug text-mavi-acik">{kucuk}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Kimlik -> check-in -> KBS akisini anlatan dekoratif cizim. */
function CheckInCizimi() {
  return (
    <svg viewBox="0 0 520 300" className="w-full max-w-[520px]" role="img" aria-label="Kimlik okuma, check-in ve KBS bildirimi akışı">
      {/* Kimlik karti */}
      <g style={{ animation: 'bob 5s ease-in-out infinite', transformOrigin: '110px 130px' }}>
        <rect x="24" y="86" width="172" height="108" rx="14" fill="#132043" stroke="#2a3b6b" />
        <circle cx="62" cy="124" r="17" fill="#1f4fd8" fillOpacity=".35" stroke="#8fb0ff" strokeOpacity=".5" />
        <path d="M55 128a7 7 0 0 1 14 0" stroke="#8fb0ff" strokeWidth="1.6" fill="none" />
        <circle cx="62" cy="119" r="4.5" fill="#8fb0ff" />
        <rect x="90" y="112" width="82" height="8" rx="4" fill="#3b4d7d" />
        <rect x="90" y="128" width="60" height="8" rx="4" fill="#2a3b6b" />
        <rect x="42" y="156" width="130" height="7" rx="3.5" fill="#2a3b6b" />
        <rect x="42" y="170" width="98" height="7" rx="3.5" fill="#2a3b6b" />
        <g style={{ animation: 'scanline 2.6s ease-in-out infinite', transformOrigin: 'center' }}>
          <rect x="30" y="96" width="160" height="2.5" rx="1.25" fill="#f2b632" />
          <rect x="30" y="98" width="160" height="16" fill="url(#tarama)" />
        </g>
        <defs>
          <linearGradient id="tarama" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f2b632" stopOpacity=".35" />
            <stop offset="1" stopColor="#f2b632" stopOpacity="0" />
          </linearGradient>
        </defs>
      </g>

      {/* Akis oku */}
      <path
        d="M206 140h74"
        stroke="#8fb0ff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="8 6"
        style={{ animation: 'dash 1.2s linear infinite' }}
      />
      <path d="m276 134 8 6-8 6" stroke="#8fb0ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />

      {/* Check-in ekrani */}
      <g>
        <rect x="296" y="60" width="196" height="160" rx="16" fill="#ffffff" fillOpacity=".97" />
        <rect x="314" y="80" width="72" height="9" rx="4.5" fill="#0b1530" />
        <rect x="314" y="98" width="120" height="7" rx="3.5" fill="#c9d3ea" />
        {[0, 1, 2].map((i) => (
          <rect
            key={i}
            x="314"
            y={122 + i * 26}
            width={158 - i * 34}
            height="14"
            rx="7"
            fill={i === 2 ? '#12a150' : '#edf2ff'}
            style={{
              animation: `growY .5s ${0.4 + i * 0.25}s cubic-bezier(.2,.8,.2,1) both`,
              transformOrigin: `314px ${129 + i * 26}px`,
            }}
          />
        ))}
        <rect x="314" y="192" width="76" height="14" rx="7" fill="#1f4fd8" />
      </g>

      {/* KBS rozeti */}
      <g style={{ animation: 'popin .5s 1.3s cubic-bezier(.2,.8,.2,1) both' }}>
        <rect x="330" y="232" width="182" height="40" rx="20" fill="#12a150" fillOpacity=".16" stroke="#12a150" strokeOpacity=".45" />
        <circle cx="354" cy="252" r="9" fill="#12a150" />
        <path d="m350 252 3 3 5-5.5" stroke="#0b1530" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <text x="372" y="257" fill="#ffffff" fontSize="13" fontWeight="600" fontFamily="inherit">
          KBS bildirimi gönderildi
        </text>
      </g>

      {/* Anahtar ve oda durumu */}
      <g style={{ animation: 'bob 4.2s .8s ease-in-out infinite', transformOrigin: '150px 240px' }}>
        <circle cx="126" cy="238" r="13" fill="none" stroke="#f2b632" strokeWidth="2.5" />
        <path d="M138 238h34m-8 0v9m-12-9v6" stroke="#f2b632" strokeWidth="2.5" strokeLinecap="round" />
        <text x="24" y="278" fill="#8fb0ff" fontSize="13" fontFamily="inherit">
          Oda 201 · hazır
        </text>
      </g>
    </svg>
  );
}
